import { TimeBucket, TimeBucket_TimeSeries } from '../../proto/generated/common'

export type PivotType = 'Traditional' | 'Fibonacci' | 'Woodie' | 'Classic' | 'DM' | 'Camarilla'

export type PivotTimeframe =
    | 'Auto' | 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly'
    | 'Biyearly' | 'Triyearly' | 'Quinquennially' | 'Decennially'

export type PivotCandle = { time: number, open: number, high: number, low: number, close: number }

export type PivotDataPoint = { startTime: number, endTime: number, val: number }

export type PivotLevel = { label: string, dataPoints: PivotDataPoint[] }

export const PIVOT_LEVEL_LABELS = ['P', 'R1', 'S1', 'R2', 'S2', 'R3', 'S3', 'R4', 'S4', 'R5', 'S5']

const IST_OFFSET_SECONDS = 19800
const DAY_SECONDS = 86400

type ResolvedTimeframe = Exclude<PivotTimeframe, 'Auto'>

/** The pivot period the chart interval maps to when the timeframe is "Auto". */
export const resolvePivotTimeframe = (timeframe: PivotTimeframe, chartTimeBucket?: TimeBucket): ResolvedTimeframe => {
    if (timeframe !== 'Auto') return timeframe
    if (!chartTimeBucket) return 'Daily'

    const { timeSeries, value } = chartTimeBucket
    switch (timeSeries) {
        case TimeBucket_TimeSeries.SECOND:
            return value <= 15 * 60 ? 'Daily' : 'Weekly'
        case TimeBucket_TimeSeries.MINUTE:
            return value <= 15 ? 'Daily' : 'Weekly'
        case TimeBucket_TimeSeries.HOUR:
            return 'Weekly'
        case TimeBucket_TimeSeries.DAY:
            return 'Monthly'
        default:
            return 'Yearly'
    }
}

const YEAR_MULTIPLIER: Partial<Record<ResolvedTimeframe, number>> = {
    Yearly: 1,
    Biyearly: 2,
    Triyearly: 3,
    Quinquennially: 5,
    Decennially: 10,
}

/** Candles the periods are built from: daily for daily and weekly pivots, monthly for the longer ones. */
export const getPivotBaseTimeBucket = (timeframe: ResolvedTimeframe): TimeBucket => {
    const daily = timeframe === 'Daily' || timeframe === 'Weekly'
    return { timeSeries: daily ? TimeBucket_TimeSeries.DAY : TimeBucket_TimeSeries.MONTH, value: 1 }
}

const getBaseCandlesPerPeriod = (timeframe: ResolvedTimeframe): number => {
    switch (timeframe) {
        case 'Daily':
            return 1
        case 'Weekly':
            return 5
        case 'Monthly':
            return 1
        case 'Quarterly':
            return 3
        default:
            return 12 * (YEAR_MULTIPLIER[timeframe] ?? 1)
    }
}

/**
 * Base candles to request: one period before the oldest pivot for its levels, and one more
 * because the oldest period fetched is usually cut short and gets dropped.
 */
export const getPivotBaseCandleCount = (timeframe: ResolvedTimeframe, pivotsBack: number): number =>
    (pivotsBack + 2) * getBaseCandlesPerPeriod(timeframe)

/** Identifies the pivot period a candle belongs to. `time` is in seconds; dates are taken in IST. */
export const getPivotPeriodKey = (time: number, timeframe: ResolvedTimeframe): number => {
    const istSeconds = time + IST_OFFSET_SECONDS
    const days = Math.floor(istSeconds / DAY_SECONDS)
    if (timeframe === 'Daily') return days
    // 1970-01-01 was a Thursday; shifting by 3 days starts each week on Monday.
    if (timeframe === 'Weekly') return Math.floor((days + 3) / 7)

    const date = new Date(istSeconds * 1000)
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth()
    if (timeframe === 'Monthly') return year * 12 + month
    if (timeframe === 'Quarterly') return year * 4 + Math.floor(month / 3)
    return Math.floor(year / (YEAR_MULTIPLIER[timeframe] ?? 1))
}

/** Rough length of a period, used to extend the latest pivot to the end of its period. */
const getPivotPeriodSeconds = (timeframe: ResolvedTimeframe): number => {
    switch (timeframe) {
        case 'Daily':
            return DAY_SECONDS
        case 'Weekly':
            return 7 * DAY_SECONDS
        case 'Monthly':
            return 31 * DAY_SECONDS
        case 'Quarterly':
            return 92 * DAY_SECONDS
        default:
            return 366 * DAY_SECONDS * (YEAR_MULTIPLIER[timeframe] ?? 1)
    }
}

export const aggregatePivotPeriods = (candles: PivotCandle[], timeframe: ResolvedTimeframe): PivotCandle[] => {
    const periods: PivotCandle[] = []
    let currentKey: number | undefined
    candles.forEach((candle) => {
        if (![candle.open, candle.high, candle.low, candle.close].every(Number.isFinite)) return
        if (candle.high === 0 && candle.low === 0 && candle.close === 0) return

        const key = getPivotPeriodKey(candle.time, timeframe)
        const current = periods[periods.length - 1]
        if (!current || key !== currentKey) {
            currentKey = key
            periods.push({ ...candle })
            return
        }
        current.high = Math.max(current.high, candle.high)
        current.low = Math.min(current.low, candle.low)
        current.close = candle.close
    })
    return periods
}

/**
 * Levels of one period, from the previous period's open/high/low/close. Woodie also uses the open
 * of the period the levels are drawn on. Levels a type does not define are left out.
 */
export const calculatePivotLevels = (
    type: PivotType,
    previous: PivotCandle,
    currentOpen: number,
): Record<string, number> => {
    const { open: o, high: h, low: l, close: c } = previous
    const range = h - l

    switch (type) {
        case 'Fibonacci': {
            const p = (h + l + c) / 3
            return {
                P: p,
                R1: p + 0.382 * range, S1: p - 0.382 * range,
                R2: p + 0.618 * range, S2: p - 0.618 * range,
                R3: p + range, S3: p - range,
            }
        }
        case 'Woodie': {
            const p = (h + l + 2 * currentOpen) / 4
            const r3 = h + 2 * (p - l)
            const s3 = l - 2 * (h - p)
            return {
                P: p,
                R1: 2 * p - l, S1: 2 * p - h,
                R2: p + range, S2: p - range,
                R3: r3, S3: s3,
                R4: r3 + range, S4: s3 - range,
            }
        }
        case 'Classic': {
            const p = (h + l + c) / 3
            return {
                P: p,
                R1: 2 * p - l, S1: 2 * p - h,
                R2: p + range, S2: p - range,
                R3: p + 2 * range, S3: p - 2 * range,
                R4: p + 3 * range, S4: p - 3 * range,
            }
        }
        case 'DM': {
            const x = o === c ? h + l + 2 * c : c > o ? 2 * h + l + c : 2 * l + h + c
            return { P: x / 4, R1: x / 2 - l, S1: x / 2 - h }
        }
        case 'Camarilla': {
            const r5 = (h / l) * c
            return {
                P: (h + l + c) / 3,
                R1: c + 1.1 * range / 12, S1: c - 1.1 * range / 12,
                R2: c + 1.1 * range / 6, S2: c - 1.1 * range / 6,
                R3: c + 1.1 * range / 4, S3: c - 1.1 * range / 4,
                R4: c + 1.1 * range / 2, S4: c - 1.1 * range / 2,
                R5: r5, S5: c - (r5 - c),
            }
        }
        default: {
            const p = (h + l + c) / 3
            return {
                P: p,
                R1: 2 * p - l, S1: 2 * p - h,
                R2: p + range, S2: p - range,
                R3: 2 * p + (h - 2 * l), S3: 2 * p - (2 * h - l),
                R4: 3 * p + (h - 3 * l), S4: 3 * p - (3 * h - l),
                R5: 4 * p + (h - 4 * l), S5: 4 * p - (4 * h - l),
            }
        }
    }
}

/**
 * Pivot lines for the last `pivotsBack` periods. Every label is returned, with no data points
 * for the levels the type does not define. Candle times are in seconds, line times in milliseconds.
 */
export const buildPivotLevels = (
    baseCandles: PivotCandle[],
    type: PivotType,
    timeframe: ResolvedTimeframe,
    pivotsBack: number,
    dropFirstPeriod: boolean,
): PivotLevel[] => {
    let periods = aggregatePivotPeriods(baseCandles, timeframe)
    if (dropFirstPeriod && periods.length > 2) periods = periods.slice(1)

    const levels: PivotLevel[] = PIVOT_LEVEL_LABELS.map((label) => ({ label, dataPoints: [] }))
    const firstPeriod = Math.max(1, periods.length - pivotsBack)

    for (let i = firstPeriod; i < periods.length; i++) {
        const period = periods[i]
        const next = periods[i + 1]
        const startTime = period.time * 1000
        const endTime = (next ? next.time : period.time + getPivotPeriodSeconds(timeframe)) * 1000
        const values = calculatePivotLevels(type, periods[i - 1], period.open)

        levels.forEach((level) => {
            const val = values[level.label]
            if (val === undefined || !Number.isFinite(val)) return
            level.dataPoints.push({ startTime, endTime, val })
        })
    }
    return levels
}
